import type {
  LabeledOption,
  Nullable,
  ShopperReservationMode,
  StoreType,
} from '@iheartjane/types';

import type { BloomMenuStore } from '@jane/bloom/dataAccess/roots/storeApi/bloom/menu.server';
import type { ShoppingPreferences } from '@jane/bloom/loaders/rootLoader';
import type { StoreViewModel } from '@jane/bloom/viewModels/store';

export type StoreSwitcherShoppingPreferences = Pick<
  ShoppingPreferences,
  | 'city'
  | 'countryCode'
  | 'latitude'
  | 'longitude'
  | 'reservationMode'
  | 'state'
  | 'streetAddress'
  | 'streetAddress2'
  | 'userEnteredAddress'
  | 'zipcode'
>;

export type StoreSwitcher = {
  addressSuggestions: LabeledOption[];
  availableStores: BloomMenuStore[];
  busy: boolean;
  deliveryStores: BloomMenuStore[];
  displayAddressSuggestions: boolean;
  handleSearchAddressChanged: (newAddress: string) => void;
  handleSearchReservationModeChanged: (newMode: ShopperReservationMode) => void;
  hasPinnedStoreId: boolean;
  inDeliveryRange: boolean;
  isUndeliverableAddress: boolean;
  isVisible: boolean;
  locationButtonDisabled: boolean;
  medStores: BloomMenuStore[];
  pickupStores: BloomMenuStore[];
  pinnedStore: Nullable<StoreViewModel>;
  pinning: boolean;
  recStores: BloomMenuStore[];
  reservationModeWarningShown: boolean;
  searchAddress: string;
  searchReservationMode: ShopperReservationMode;
  selectSuggestedAddress: (newAddress: string) => void;
  setAddressSuggestions: (newSuggestions: LabeledOption[]) => void;
  setBusy: (busy: boolean) => void;
  setLocationButtonDisabled: (state: boolean) => void;
  setPinnedStore: (storeId: number) => void;
  setReservationModeWarningShown: (state: boolean) => void;
  setStoreType: (newType: StoreType) => void;
  storeType: StoreType;
  toggleVisibility: () => void;
  updateShoppingPreferences: (
    updates: Partial<StoreSwitcherShoppingPreferences>
  ) => void;
  validateAddress: (address: string, mode: ShopperReservationMode) => void;
  validationState: 'submitting' | 'idle' | 'loading';
};

/**
 * Defined separately so that the hook can be mocked while allowing
 * you to fill in the rest of the context in tests.
 */
export const defaultStoreSwitcherContext: StoreSwitcher = {
  addressSuggestions: [],
  availableStores: [],
  busy: false,
  deliveryStores: [],
  displayAddressSuggestions: false,
  handleSearchAddressChanged: () => {},
  handleSearchReservationModeChanged: () => {},
  hasPinnedStoreId: false,
  inDeliveryRange: false,
  isUndeliverableAddress: false,
  isVisible: false,
  locationButtonDisabled: false,
  medStores: [],
  pickupStores: [],
  pinnedStore: null,
  pinning: false,
  recStores: [],
  reservationModeWarningShown: false,
  searchAddress: '',
  searchReservationMode: 'pickup',
  selectSuggestedAddress: () => {},
  setAddressSuggestions: () => {},
  setBusy: () => {},
  setLocationButtonDisabled: () => {},
  setPinnedStore: () => {},
  setReservationModeWarningShown: () => {},
  setStoreType: () => {},
  storeType: 'all',
  toggleVisibility: () => {},
  updateShoppingPreferences: () => {},
  validateAddress: () => {},
  validationState: 'idle',
};
